import { borderStyles, fontStyles, globalStyles } from 'styles/theme';

import { Articulation } from 'types/articulation';
import { Button } from 'antd';
import CaptionText from 'components/common/CaptionText';
import { Course } from 'types/course';
import { CourseCohort } from '@uac-au/advance-typings';
import FlexContainer from 'components/common/FlexContainer';
import { Fragment } from 'react';
import ImageWithFilter from 'components/common/ImageWithFilter';
import { interpolate } from 'utils/interpolate';
import { mobileMediaQuery } from 'utils/common';
import styled from '@emotion/styled';
import useTranslation from 'hooks/use-translation';

export const ButtonContainer = styled(FlexContainer)({
  minWidth: 164,
});

export const ViewCreditButton = styled(Button)({
  minHeight: 36,
});

export const ArrowContainer = styled.div({
  paddingTop: 24,
  [mobileMediaQuery]: {
    paddingTop: 20,
  },
});

export const TargetArticulationsContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  [mobileMediaQuery]: {
    flexDirection: 'column',
    flex: 'initial',
    justifyContent: 'initial',
    gap: 20,
  },
});

export const TargetArticulationsRightContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  flex: 1,
});

export const TargetArticulationContainer = styled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  borderBottom: `1px solid ${borderStyles.colors.primary}`,
  padding: '20px 24px 20px 0px',
  '&:last-of-type': {
    borderBottom: 'initial',
  },
});

type TargetCourseContentProps = {
  course: Course;
};

export function TargetCourseContent(props: TargetCourseContentProps): JSX.Element {
  const {
    course: { name, url, code },
  } = props;

  const courseName = <CourseName className='target-course-label'>{name}</CourseName>;

  const translation = useTranslation();
  if (!url) {
    return courseName;
  }

  return (
    <Anchor href={url} target='_blank' rel='noopener noreferrer'>
      {translation ? interpolate(translation.subjectFormat, { name, code }) : name}
    </Anchor>
  );
}

const Anchor = styled.a({
  display: 'flex',
});

const CourseName = styled.span({
  color: fontStyles.colors.primary,
  fontWeight: fontStyles.weights.medium,
});

type CricosCodeContentProps = {
  course: Course;
};

export function CricosCodeContent(props: CricosCodeContentProps): JSX.Element | null {
  const {
    course: { cricosCodes },
  } = props;

  if (cricosCodes.length === 0) {
    return null;
  }

  const cricosString = `CRICOS ${cricosCodes.join(', ')}`;
  return (
    <CaptionText className='cricos-label' aria-hidden='true'>
      {cricosString}
    </CaptionText>
  );
}

type CohortContentProps = {
  articulation: Articulation;
};

export function CohortContent(props: CohortContentProps): JSX.Element | null {
  const { articulation } = props;

  const translation = useTranslation();
  if (!translation) {
    return null;
  }

  return (
    <Fragment>
      {articulation.course.cohorts.map((cohort) => {
        const cohortKey = cohort === CourseCohort.INTERNATIONAL ? 'international' : 'domestic';
        const { filter, label } = translation.common.cohorts[cohortKey];
        return (
          <FlexContainer flexDirection='row' gap={4} key={cohort} aria-hidden='true'>
            <ImageWithFilter
              src={`./config/icons/${translation.common.icons[cohortKey].src}`}
              alt={translation.common.icons[cohortKey].alt}
              width={16}
              filter={filter}
            />
            <CohortCaptionText className='cohort-label'>{label}</CohortCaptionText>
          </FlexContainer>
        );
      })}
    </Fragment>
  );
}

const CohortCaptionText = styled(CaptionText)({
  color: fontStyles.colors.primary,
});

type AdmissionCriteriaContentProps = {
  articulation: Articulation;
};

export function AdmissionCriteriaContent(props: AdmissionCriteriaContentProps): JSX.Element | null {
  const { articulation } = props;
  const translation = useTranslation();
  if (!translation) {
    return null;
  }

  const additionalAdmissionCriteria = articulation.arrangements.some(
    (arrangement) => arrangement.additionalAdmissionCriteria,
  );

  if (!additionalAdmissionCriteria) {
    return null;
  }

  return (
    <FlexContainer gap={4} flexDirection='row' alignItems='center' aria-hidden='true'>
      <img
        src={`config/icons/${translation.common.icons.infoFilled.src}`}
        alt={translation.common.icons.infoFilled.alt}
        width={16}
        height={16}
      />
      <CaptionText className='admission-criteria-label'>Admission criteria applies</CaptionText>
    </FlexContainer>
  );
}

type ViewCourseContentProps = {
  course: Course;
};

export function ViewCourseContent(props: ViewCourseContentProps): JSX.Element | null {
  const { course } = props;
  const translation = useTranslation();
  if (!translation) {
    return null;
  }

  if (!course.url) {
    return null;
  }

  return (
    <a
      href={course.url}
      target='_blank'
      rel='noopener noreferrer'
      aria-label={`View course information about ${course.name}`}
    >
      <FlexContainer gap={4} flexDirection='row' alignItems='center'>
        <img src={`./config/icons/${translation.common.icons.externalLink.src}`} alt='' width={16} height={16} />
        <CourseCaptionText className='view-course-link'>
          {translation.home.searchResultTab.viewArticulationsTab.viewButton}
        </CourseCaptionText>
      </FlexContainer>
    </a>
  );
}

const CourseCaptionText = styled(CaptionText)({
  color: globalStyles.colors.primary,
});
